import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTimeForList } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { EmailThreadMessage } from '../useData';
import { useEmailThread } from '../useData';

interface EmailThreadPanelProps {
  emailId: number;
  partitionAt?: string | null;
  /** Same filters + pagination as the inbox list (thread view) */
  listScopeQuery?: string | null;
  onSelectMessage: (message: { id: number; partitionAt?: string }) => void;
}

function addrEmail(a: unknown): string {
  if (!a) return '';
  if (typeof a === 'string') return a;
  if (Array.isArray(a)) return addrEmail(a[0]);
  if (typeof a === 'object' && a !== null) {
    const obj = a as { email?: string; address?: string };
    return obj.email ?? obj.address ?? '';
  }
  return '';
}

function addrName(a: unknown): string {
  if (!a) return '';
  if (typeof a === 'string') return a;
  if (Array.isArray(a)) return addrName(a[0]);
  if (typeof a === 'object' && a !== null) {
    const obj = a as { name?: string; email?: string; address?: string };
    return obj.name?.trim() || addrEmail(obj) || '';
  }
  return '';
}

function senderLabel(from: EmailThreadMessage['from']): string {
  const raw = Array.isArray(from) ? from[0] : from;
  const email = addrEmail(raw);
  const name = addrName(raw);
  return name || (email ? email.split('@')[0] : '') || 'Unknown';
}

const EmailThreadPanel: React.FC<EmailThreadPanelProps> = ({
  emailId,
  partitionAt,
  listScopeQuery,
  onSelectMessage,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation('emails');
  const listScoped = listScopeQuery != null && listScopeQuery.length > 0;
  const { thread, isLoading } = useEmailThread(
    emailId,
    partitionAt,
    listScoped ? listScopeQuery : null,
  );
  const [expanded, setExpanded] = useState(true);
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const messages = useMemo(() => {
    const source = thread?.emails ?? [];
    return [...source].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
  }, [thread?.emails]);
  const count = messages.length;
  const showPanel = count > 1;
  const THREAD_AUTO_COLLAPSE_THRESHOLD = 6;

  const currentIndex = useMemo(
    () => messages.findIndex((m) => m.id === emailId),
    [messages, emailId],
  );

  useEffect(() => {
    if (!showPanel) return;
    setExpanded(count <= THREAD_AUTO_COLLAPSE_THRESHOLD);
  }, [emailId, showPanel, count]);

  useEffect(() => {
    if (!expanded || currentIndex < 0) return;
    const msg = messages[currentIndex];
    const key = `${msg.id}-${msg.partitionAt}`;
    const el = rowRefs.current.get(key);
    if (el) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [expanded, currentIndex, messages, emailId]);

  const title = useMemo(() => {
    if (isLoading && count <= 1) return t('threadLoadingTitle');
    return t('threadTitle', { count: messages.length > 0 ? messages.length : count });
  }, [count, isLoading, messages.length, t]);

  const goToIndex = (index: number) => {
    const msg = messages[index];
    if (!msg || msg.id === emailId) return;
    onSelectMessage({ id: msg.id, partitionAt: msg.partitionAt });
  };

  const goPrev = () => {
    if (currentIndex > 0) goToIndex(currentIndex - 1);
  };

  const goNext = () => {
    if (currentIndex >= 0 && currentIndex < messages.length - 1) goToIndex(currentIndex + 1);
  };

  if (!showPanel && !isLoading) return null;

  const positionLabel =
    currentIndex >= 0
      ? t('threadPosition', { current: currentIndex + 1, total: messages.length })
      : null;

  return (
    <div
      className={cn(
        'flex-shrink-0 border-b print:hidden',
        colors.border,
        'bg-indigo-50/40 dark:bg-indigo-950/15',
      )}
    >
      <div className="flex items-center gap-1 px-2 py-1 min-h-[32px]">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={cn(
            'flex items-center gap-1.5 min-w-0 flex-1 text-left text-[11px] font-medium rounded px-1 py-0.5',
            colors.text,
            colors.interactiveHover,
          )}
          aria-expanded={expanded}
        >
          <svg className="h-3.5 w-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1 1 0 01-1-1v-3M3 8h2a2 2 0 012 2v6a2 2 0 01-2 2H5v4l-4-4H3a1 1 0 01-1-1v-3" />
          </svg>
          <span className="truncate">{title}</span>
          {positionLabel && (
            <span className={cn('shrink-0 text-[10px] font-normal', colors.textMuted)}>
              · {positionLabel}
            </span>
          )}
          <svg
            className={cn('h-3.5 w-3.5 shrink-0 transition-transform', expanded && 'rotate-180')}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {count > 1 && (
          <div className="flex items-center shrink-0 gap-0.5">
            <button
              type="button"
              onClick={goPrev}
              disabled={currentIndex <= 0}
              className={cn(
                'h-6 w-6 rounded flex items-center justify-center disabled:opacity-30',
                colors.textMuted,
                colors.interactiveHover,
              )}
              title={t('threadPrev')}
              aria-label={t('threadPrev')}
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={currentIndex < 0 || currentIndex >= messages.length - 1}
              className={cn(
                'h-6 w-6 rounded flex items-center justify-center disabled:opacity-30',
                colors.textMuted,
                colors.interactiveHover,
              )}
              title={t('threadNext')}
              aria-label={t('threadNext')}
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="px-2 pb-1.5 max-h-36 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
          {isLoading && messages.length === 0 && (
            <p className={cn('px-1 py-0.5 text-[11px]', colors.textMuted)}>{t('loading')}</p>
          )}
          <div className={cn('rounded-md border overflow-hidden', colors.border, colors.surface)}>
            {messages.map((msg, index) => {
              const isCurrent = msg.id === emailId;
              const fromLabel = senderLabel(msg.from);
              const rowKey = `${msg.id}-${msg.partitionAt}`;
              const subject = msg.subject?.trim() || t('noSubject');
              return (
                <button
                  key={rowKey}
                  ref={(el) => {
                    if (el) rowRefs.current.set(rowKey, el);
                    else rowRefs.current.delete(rowKey);
                  }}
                  type="button"
                  onClick={() => {
                    if (!isCurrent) {
                      onSelectMessage({ id: msg.id, partitionAt: msg.partitionAt });
                    }
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1 text-left text-[11px] leading-tight transition-colors border-b last:border-b-0',
                    colors.border,
                    isCurrent
                      ? 'bg-indigo-100/90 dark:bg-indigo-950/40 font-semibold'
                      : colors.interactiveHover,
                  )}
                  aria-current={isCurrent ? 'true' : undefined}
                  title={`${index + 1}. ${fromLabel} — ${subject} (${formatDateTimeForList(msg.date)})`}
                >
                  <span className={cn('shrink-0 w-4 text-right tabular-nums text-[10px]', colors.textMuted)}>
                    {index + 1}
                  </span>
                  {!msg.isRead ? (
                    <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" aria-hidden />
                  ) : (
                    <span className="inline-block h-1.5 w-1.5 shrink-0" aria-hidden />
                  )}
                  <span
                    className={cn(
                      'shrink-0 max-w-[28%] truncate',
                      isCurrent ? cn(colors.text, 'font-semibold') : !msg.isRead ? colors.text : colors.textSecondary,
                    )}
                  >
                    {fromLabel}
                  </span>
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate',
                      isCurrent ? cn(colors.text, 'font-semibold') : colors.textMuted,
                    )}
                  >
                    {subject}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 text-[10px] whitespace-nowrap',
                      isCurrent ? cn(colors.text, 'font-semibold') : colors.textMuted,
                    )}
                  >
                    {formatDateTimeForList(msg.date)}
                  </span>
                  {isCurrent && (
                    <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
                      {t('viewing')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailThreadPanel;
